import { api_configuration } from "../../common";
import DestructorTree from "../DestructorTree";
import { addMessage } from "../front/messages";
import { DataStoreProtocolError } from "../interface/DatastoreProxy";
import {
	ControlCollectionProperty,
	ControlControlProperty,
	ControlInstructionProperty,
	ControlMarkupProperty,
	ControlNumberProperty,
	ControlProperties,
	ControlProperty,
	ControlStateGroupProperty,
	ControlTextProperty,
} from "./ControlLibrary";
import { SortedKeys } from "./ExpressionTypes";
import ImplementationVisitorContext from "./ImplementationVisitorContext";
import { InstructionError } from "./instructions/InstructionError";
import { visit_control_binding } from "./visit_control_binding";
import { visitOneOrMoreExpression } from "./visit_widget_implementation_expression";
import api_controls = api_configuration.libraries.gui_definition_lib.libraries.control_bundle_lib.libraries.controls_lib.types.control_library.alan_api;
import api_widget = api_configuration.libraries.gui_definition_lib.types.widget.alan_api;

function makeInstruction(
	expression: api_widget.Cone_or_more_expression,
	implementation_context: ImplementationVisitorContext,
	destructor: DestructorTree
): (event: any) => Promise<void> {
	let local_implementation_context: ImplementationVisitorContext | undefined = implementation_context;
	const instruction_expression = visitOneOrMoreExpression(expression);

	let handler = async (event: any) => {
		// Do not execute the instruction if the current context is destroyed. But do not stop executing the instructions
		// once started. That's why the instruction_destructor is not a child of the destructor.
		if (local_implementation_context === undefined) {
			return;
		}
		const instruction_destructor = DestructorTree.create();
		try {
			await instruction_expression.execute(local_implementation_context, instruction_destructor, event);
		} catch (reason) {
			if (reason instanceof DataStoreProtocolError) {
				addMessage("warning", "An error occurred");
			} else if (!(reason instanceof InstructionError)) {
				throw reason;
			}
		}
		instruction_destructor.destroy();
	};

	destructor.onDestroy(() => {
		local_implementation_context = undefined;
		handler = async () => {
			// set the handler to a noop function so that the context can be garbage collected.
		};
	});

	return async (event: any) => {
		await handler(event);
	};
}

const renderQueue: {
	widget_implementation_node: api_widget.Cwidget_implementation_node;
	control_context: ControlProperties;
	implementation_context: ImplementationVisitorContext;
	destructor: DestructorTree;
}[] = [];
let visiting_widget_implementation = false;
export function visit_widget_implementation_node(
	widget_implementation_node: api_widget.Cwidget_implementation_node,
	control_context: ControlProperties,
	implementation_context: ImplementationVisitorContext,
	destructor: DestructorTree
): void {
	renderQueue.push({
		widget_implementation_node: widget_implementation_node,
		control_context: control_context,
		implementation_context: implementation_context,
		destructor: destructor,
	});
	if (!visiting_widget_implementation) {
		visiting_widget_implementation = true;
		let head = 0;
		while (head !== renderQueue.length) {
			const item = renderQueue[head];
			head++;
			if (!item.destructor.destroyed) {
				visitAttributes(
					item.widget_implementation_node,
					item.control_context,
					item.implementation_context,
					item.destructor
				);
			}
		}
		renderQueue.splice(0);
		visiting_widget_implementation = false;
	}
}

function visitAttributes(
	widget_implementation_node: api_widget.Cwidget_implementation_node,
	control_context: ControlProperties,
	implementation_context: ImplementationVisitorContext,
	destructor: DestructorTree
) {
	for (const attribute of widget_implementation_node.input.context_control_node().properties.attributes.values()) {
		const current_control_context = control_context[attribute.key];
		if (current_control_context === undefined) {
			throw new Error("Missing property in control node: " + attribute.key);
		}
		const widget_attribute = widget_implementation_node.properties.attributes.get(attribute.key)?.properties.attribute;
		if (widget_attribute === undefined) {
			applyControlDefaults(attribute, current_control_context, destructor);
		} else {
			visitAttribute(widget_attribute, current_control_context, implementation_context, destructor);
		}
	}
}

export function visitAttribute(
	attribute: api_widget.Cwidget_implementation_attribute,
	control_context: ControlProperty,
	implementation_context: ImplementationVisitorContext,
	destructor: DestructorTree
): void {
	const control_attribute = attribute.input.attribute();
	switch (control_attribute.properties.type.state.name) {
		case "instruction": {
			control_context = control_context as ControlInstructionProperty;
			control_context(makeInstruction(attribute.properties.expression, implementation_context, destructor));
			break;
		}
		case "markup": {
			let dom_element = control_context as ControlMarkupProperty;
			if (global.alan_debug) {
				let created_element: HTMLElement;
				dom_element = () => {
					if (created_element === undefined) {
						created_element = (control_context as ControlMarkupProperty)();
						(created_element as any).alan_node = implementation_context.current_binding_context;
						if (created_element.nodeName !== "#comment") {
							created_element.setAttribute(
								"data-alan-context",
								(implementation_context.current_binding_context as any)?.path
							);
						}
					}
					return created_element;
				};
			}
			visitOneOrMoreExpression(attribute.properties.expression).subscribe(
				implementation_context,
				{
					add: (
						control_binding: api_widget.Ccontrol_binding,
						sub_implementation_context,
						sub_destructor,
						wrap_in_dynamic_area
					) => {
						visit_control_binding(
							control_binding,
							dom_element,
							sub_implementation_context,
							sub_destructor,
							wrap_in_dynamic_area
						);
					},
				},
				destructor,
				undefined
			);
			break;
		}
		case "control": {
			visitOneOrMoreExpression(attribute.properties.expression).subscribe(
				implementation_context,
				{
					add: (
						widget_implmentation_node: api_widget.Cwidget_implementation_node,
						sub_implementation_context,
						sub_destructor
					) => {
						visit_widget_implementation_node(
							widget_implmentation_node,
							(control_context as ControlControlProperty)(makeControlDestructor(sub_destructor), {}),
							sub_implementation_context,
							sub_destructor
						);
					},
				},
				destructor,
				undefined
			);
			break;
		}
		case "property": {
			const property__type__attributes = control_attribute.properties.type.state.node;
			switch (property__type__attributes.properties.type.state.name) {
				case "collection": {
					control_context = control_context as ControlCollectionProperty;
					const control_collection_entry = control_context.initialize(makeControlDestructor(destructor), {
						entries: new Map(),
						sort: [],
					});

					let sort: (() => void) | undefined = undefined;
					let sorting: SortedKeys = [];
					if (control_collection_entry.sort !== undefined) {
						const flatAndSort = () => {
							const sorted = flatDeep(sorting, Infinity) as string[];
							control_collection_entry.sort!(sorted);
						};
						destructor.onDestroy(() => {
							sort_callbacks.delete(flatAndSort);
						});
						sort = () => {
							requestSort(flatAndSort);
						};
					}
					visitOneOrMoreExpression(attribute.properties.expression).subscribe(
						implementation_context,
						{
							add: async (
								value: api_widget.Cwidget_implementation_node | undefined,
								entry_implementation_context,
								entry_destructor,
								_wrap,
								key: string
							) => {
								if (value !== undefined) {
									const entry_contract = control_collection_entry.add(key, makeControlDestructor(entry_destructor), {});
									entry_destructor.onDestroy(() => {
										entry_contract.remove();
									});
									visit_widget_implementation_node(
										value,
										entry_contract.entry,
										entry_implementation_context,
										entry_destructor
									);
								}
							},
							update: (s) => {
								sorting = s;
								sort?.();
							},
						},
						destructor,
						expressionIdGenerator()
					);
					break;
				}
				case "number": {
					visitOneOrMoreExpression(attribute.properties.expression).subscribe(
						implementation_context,
						{
							add: async (value: number) => {
								(control_context as ControlNumberProperty)(value);
							},
						},
						destructor,
						undefined
					);
					break;
				}
				case "text": {
					const setControlValue = control_context as ControlTextProperty;

					const entries: Record<string, string> = {};
					let sorting: SortedKeys | undefined = undefined;

					let updateTextValue = () => {
						const keys = Object.keys(entries);
						if (keys.length === 0) {
							setControlValue("");
						} else if (keys.length === 1) {
							setControlValue(entries[keys[0]]);
						} else if (sorting !== undefined) {
							const flattened = flatDeep(sorting, Infinity) as string[];
							const value = flattened.map((key) => entries[key] ?? "").join("");
							setControlValue(value);
						}
					};
					destructor.onDestroy(() => {
						updateTextValue = () => {
							//Do not update control texts when the context is destroyed.
						};
					});

					visitOneOrMoreExpression(attribute.properties.expression).subscribe(
						implementation_context,
						{
							add: async (value: string, _i, text_value_destructor, _w, key: string) => {
								if (value === undefined) {
									if (entries[key] !== undefined) {
										delete entries[key];
										updateTextValue();
									}
								} else {
									entries[key] = value.toString();
									updateTextValue();
								}
								text_value_destructor.onDestroy(() => {
									delete entries[key];
									updateTextValue();
								});
							},
							update: (s) => {
								sorting = s;
								updateTextValue();
							},
						},
						destructor,
						expressionIdGenerator()
					);
					break;
				}
				case "state group": {
					visitOneOrMoreExpression(attribute.properties.expression).subscribe(
						implementation_context,
						{
							add: async (select_state: api_widget.Cstate__for, sub_implementation_context, sub_destructor) => {
								visit_widget_implementation_node(
									select_state.parent.properties.node,
									(control_context as ControlStateGroupProperty)[select_state.properties.state.entry](
										makeControlDestructor(sub_destructor),
										{}
									),
									sub_implementation_context,
									sub_destructor
								);
							},
						},
						destructor,
						undefined
					);
					break;
				}
				default:
					unexpectedState(property__type__attributes.properties.type.state);
			}
			break;
		}
		default:
			unexpectedState(control_attribute.properties.type.state);
	}
}

const sort_callbacks = new Set<() => void>();
let sorting = false;
function requestSort(callback: () => void) {
	sort_callbacks.add(callback);
	if (sorting) {
		return;
	}
	sorting = true;
	requestAnimationFrame(() => {
		for (const callback of sort_callbacks) {
			callback();
		}
		sort_callbacks.clear();
		sorting = false;
	});
}

export function expressionIdGenerator() {
	let counter = 0;
	return () => {
		const newId = counter.toString();
		counter += 1;
		return newId;
	};
}

function applyControlDefaults(
	attribute: api_controls.Cattributes__control_node,
	current_control_context: ControlProperty,
	destructor: DestructorTree
) {
	switch (attribute.properties.type.state.name) {
		case "instruction":
			break;
		case "markup":
			break;
		case "control": {
			const control_attribute = attribute.properties.type.state.node;
			const contract = (current_control_context as ControlControlProperty)(makeControlDestructor(destructor), {});
			applyControlImplementation(
				control_attribute.properties.has_default.cast("yes").properties.implementation,
				contract,
				destructor
			);
			break;
		}
		case "property": {
			const property = attribute.properties.type.state.node;
			switch (property.properties.type.state.name) {
				case "collection":
					break;
				case "number": {
					const number_type = property.properties.type.state.node;
					const number_default = number_type.properties.has_default.cast("yes");
					switch (number_default.properties.intialize.state.name) {
						case "no":
							break;
						case "yes": {
							const yes_intialize = number_default.properties.intialize.state.node;
							(current_control_context as ControlNumberProperty)(yes_intialize.properties.value.value);
							break;
						}
					}

					break;
				}
				case "text": {
					const text_type = property.properties.type.state.node;
					const text_default = text_type.properties.has_default.cast("yes");
					switch (text_default.properties.intialize.state.name) {
						case "no":
							break;
						case "yes": {
							const yes_intialize = text_default.properties.intialize.state.node;
							(current_control_context as ControlTextProperty)(yes_intialize.properties.value);
							break;
						}
					}
					break;
				}
				case "state group": {
					const state_group_type = property.properties.type.state.node;
					const state_group_default = state_group_type.properties.has_default.cast("yes");
					switch (state_group_default.properties.intialize.state.name) {
						case "no":
							break;
						case "yes": {
							const selected_state = state_group_default.properties.state;
							const state_control_context = (current_control_context as ControlStateGroupProperty)[
								selected_state.entry
							](makeControlDestructor(destructor), {});
							for (const state_attribute of selected_state.ref.properties.node.properties.attributes.values()) {
								applyControlDefaults(state_attribute, state_control_context[state_attribute.key], destructor);
							}
						}
					}

					break;
				}
				default:
					unexpectedState(property.properties.type.state);
			}
			break;
		}
		default:
			unexpectedState(attribute.properties.type.state);
	}
}

function applyControlImplementation(
	control_default_attribute: api_controls.Ccontrol_implementation,
	current_control_contex: ControlProperties,
	destructor: DestructorTree
) {
	for (const attribute of control_default_attribute.input.node().properties.attributes.values()) {
		const property_contract = current_control_contex[attribute.key];
		const implementation_attribute = control_default_attribute.properties.attributes.get(attribute.key);
		if (implementation_attribute === undefined) {
			applyControlDefaults(attribute, property_contract, destructor);
		} else {
			switch (implementation_attribute.properties.type.state.name) {
				case "control": {
					const $ = implementation_attribute.properties.type.state.node;
					applyControlImplementation(
						$.properties.implementation,
						(property_contract as ControlControlProperty)(makeControlDestructor(destructor), {}),
						destructor
					);
					break;
				}
				case "markup": {
					throw new Error("markup not supported");
				}
				case "property": {
					const property = implementation_attribute.properties.type.state.node;
					switch (property.properties.type.state.name) {
						case "collection": {
							const $ = property.properties.type.state.node;
							applyControlListEntries(
								$.properties.entries,
								(property_contract as ControlCollectionProperty).initialize(makeControlDestructor(destructor), {
									entries: new Map(),
									sort: [],
								}),
								0,
								destructor
							);
							break;
						}
						case "number": {
							const $ = property.properties.type.state.node;
							(property_contract as ControlNumberProperty)($.properties.value.value);
							break;
						}
						case "state group": {
							const $ = property.properties.type.state.node;
							applyControlImplementation(
								$.properties.implementation,
								(property_contract as ControlStateGroupProperty)[$.properties.state.entry](
									makeControlDestructor(destructor),
									{}
								),
								destructor
							);
							break;
						}
						case "text": {
							const $ = property.properties.type.state.node;
							(property_contract as ControlTextProperty)($.properties.value);
							break;
						}
						default:
							unexpectedState(property.properties.type.state);
					}
					break;
				}
				default:
					unexpectedState(implementation_attribute.properties.type.state);
			}
		}
	}
}

function applyControlListEntries(
	entries: api_controls.Centries_list,
	current_control_context: ReturnType<ControlCollectionProperty["initialize"]>,
	index: number,
	destructor: DestructorTree
) {
	switch (entries.properties.has_steps.state.name) {
		case "no":
			break;
		case "yes": {
			const $ = entries.properties.has_steps.state.node;
			applyControlImplementation(
				$.properties.implementation,
				current_control_context.add(index.toString(), makeControlDestructor(destructor), {}).entry,
				destructor
			);
			applyControlListEntries($.properties.tail, current_control_context, index + 1, destructor);
			break;
		}
		default:
			unexpectedState(entries.properties.has_steps.state);
	}
}

function makeControlDestructor(destructor: DestructorTree) {
	return (callback: () => void) => {
		destructor.onDestroy(() => {
			callback();
		});
	};
}

function flatDeep<T>(arr: T[], d = 1): T[] | T {
	return d > 0
		? arr.reduce((acc, val) => acc.concat(Array.isArray(val) ? flatDeep(val, d - 1) : val), [])
		: arr.slice();
}
